import { signInWithCustomToken, signOut } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { auth } from '../firebase/config';
import { adminApi } from '../api/admin';

const STORAGE_KEY = 'ts_admin_impersonation';
const EVENT = 'ts-impersonation-change';

export interface ImpersonationState {
  adminUid: string;
  adminEmail: string | null;
  /** Custom token Firebase pour revenir sur le compte admin (valable 1 h). */
  returnToken: string;
  target: { id: string; email: string | null; displayName: string | null };
  startedAt: number;
}

export const getImpersonation = (): ImpersonationState | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationState) : null;
  } catch {
    return null;
  }
};

const setImpersonation = (state: ImpersonationState | null) => {
  if (state) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  else sessionStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event(EVENT));
};

/**
 * Se connecter en tant qu'un utilisateur (admin uniquement).
 * Remplace la session Firebase courante par celle de la cible ; l'état admin est gardé en sessionStorage.
 */
export const startImpersonation = async (targetUserId: string): Promise<ImpersonationState> => {
  const current = auth.currentUser;
  if (!current) throw new Error('Not authenticated');

  const res = await adminApi.impersonate(targetUserId);
  const state: ImpersonationState = {
    adminUid: current.uid,
    adminEmail: current.email,
    returnToken: res.returnToken,
    target: res.target,
    startedAt: Date.now(),
  };
  setImpersonation(state);
  await signInWithCustomToken(auth, res.token);
  return state;
};

/**
 * Quitter la session « connecté en tant que » et revenir sur le compte admin.
 * Si le token de retour a expiré (> 1 h), déconnecte simplement : il faudra se reconnecter.
 */
export const stopImpersonation = async (): Promise<'restored' | 'signed-out'> => {
  const state = getImpersonation();
  setImpersonation(null);
  if (!state) return 'signed-out';

  try {
    await signInWithCustomToken(auth, state.returnToken);
    return 'restored';
  } catch {
    await signOut(auth).catch(() => undefined);
    return 'signed-out';
  }
};

export const useImpersonation = () => {
  const [state, setState] = useState<ImpersonationState | null>(() => getImpersonation());

  useEffect(() => {
    const sync = () => setState(getImpersonation());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const stop = useCallback(() => stopImpersonation(), []);
  return { impersonation: state, stopImpersonation: stop };
};

/**
 * Vérifie côté backend si l'utilisateur courant est admin (`GET /admin/me`).
 * Résultat mis en cache par UID pour éviter un appel à chaque rendu.
 */
const adminCache = new Map<string, boolean>();

export const useIsAdmin = (uid: string | null | undefined): boolean => {
  const [isAdmin, setIsAdmin] = useState<boolean>(() => (uid ? adminCache.get(uid) || false : false));

  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      setIsAdmin(false);
      return;
    }
    if (adminCache.has(uid)) {
      setIsAdmin(adminCache.get(uid) || false);
      return;
    }
    // Une session impersonée n'est jamais admin : inutile d'appeler l'API.
    if (getImpersonation()) {
      setIsAdmin(false);
      return;
    }
    adminApi
      .me()
      .then((res) => {
        adminCache.set(uid, Boolean(res?.isAdmin));
        if (!cancelled) setIsAdmin(Boolean(res?.isAdmin));
      })
      .catch(() => {
        adminCache.set(uid, false);
        if (!cancelled) setIsAdmin(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  return isAdmin;
};
